import { Box, Stack } from '@mui/material';
import { useHorizontalOverflow } from '../../../../../hooks/useHorizontalOverflow/useHorizontalOverflow';
import { TabItem } from '../tabs.types';
import { TabPill } from './TabPill';
import { TabsOverflowMenu } from './TabsOverflowMenu';

interface OverflowTabsProps {
  items: TabItem[];
  selectedId: string;
  onSelect: (id: string) => void;
}

export function OverflowTabs({ items, selectedId, onSelect }: OverflowTabsProps) {
  const recalculationKey = items
    .map((item) => `${item.id}:${item.label}:${item.secondaryLabel ?? ''}:${item.endIcon ? 1 : 0}`)
    .join('|');

  const { containerRef, measureRef, visibleCount } = useHorizontalOverflow({
    itemCount: items.length,
    recalculationKey,
    gap: 6,
    minVisible: 1,
    trailer: { mode: 'measured' },
  });

  const visibleItems = items.slice(0, visibleCount);
  const overflowItems = items.slice(visibleCount);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        minWidth: 0,
        overflow: 'clip',
        overflowClipMargin: '6px',
      }}
    >
      <Box
        ref={measureRef}
        aria-hidden
        sx={{
          display: 'flex',
          flexDirection: 'row',
          position: 'absolute',
          width: 'max-content',
          top: 0,
          left: 0,
          gap: 0.75,
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      >
        {items.map((item) => (
          <Box key={`measure-${item.id}`} sx={{ display: 'inline-flex' }}>
            <TabPill
              label={item.label}
              secondaryLabel={item.secondaryLabel}
              endIcon={item.endIcon}
            />
          </Box>
        ))}
        <TabsOverflowMenu items={items} selectedId={selectedId} onSelect={onSelect} />
      </Box>
      <Stack role="tablist" direction="row" sx={{ alignItems: 'center', gap: 0.75 }}>
        {visibleItems.map((item) => (
          <TabPill
            key={item.id}
            label={item.label}
            secondaryLabel={item.secondaryLabel}
            isActive={item.id === selectedId}
            endIcon={item.endIcon}
            tooltip={item.tooltip}
            onClick={() => onSelect(item.id)}
            testId={item.testId}
          />
        ))}
        {overflowItems.length > 0 && (
          <TabsOverflowMenu items={overflowItems} selectedId={selectedId} onSelect={onSelect} />
        )}
      </Stack>
    </Box>
  );
}
